<template>
  <v-container>
    <h2 class="text-center my-12">{{ $t("craftopia.title") }}</h2>
    <v-card class="mx-auto my-8" elevation="4" max-width="900">
      <v-img :src="craftopiaHeader" cover></v-img>

      <v-container>
        <v-form ref="form">
        <!-- お問い合わせの種類 -->
        <p class="text-label-large">{{ $t("craftopia.type") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
        <v-select placeholder="選択してください" max-width="300" :rules="[requiredValidation]" v-model="inputType"
          :items="[
            { title: $t('craftopia.bug'), value: 'bug' },
            { title: $t('craftopia.feedback'), value: 'feedback' },
            { title: $t('craftopia.idea'), value: 'idea' },
          ]"></v-select>

        <template v-if="inputType">

        <!-- プラットフォーム -->
        <template v-if="inputPlatform !== 'editor'">
          <p class="text-label-large">{{ $t("craftopia.platform") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
          <v-select placeholder="選択してください" max-width="300" :rules="[requiredValidation]" v-model="inputPlatform"
            :items="[
              { title: 'Steam', value: 'steam' },
            ]"></v-select>
        </template>
        <p v-else class="text-h6 font-weight-bold text-red">{{ $t("craftopia.editor_warning") }}</p>

        <!-- Mod使用 (type == bug の場合) -->
        <template v-if="inputType === 'bug'">
          <p class="text-label-large">{{ $t("craftopia.mod") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
          <v-select placeholder="選択してください" max-width="300" :rules="[requiredValidation]" v-model="inputModUse"
            :items="[
              { title: $t('craftopia.mod_no'), value: 'no' },
              { title: $t('craftopia.mod_past'), value: 'past' },
              { title: $t('craftopia.mod_yes'), value: 'yes' },
            ]"></v-select>
        </template>

        <!-- タイトル -->
        <p class="text-label-large">{{ $t("craftopia.title_label") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
        <v-text-field :placeholder="$t('craftopia.title_placeholder')" :rules="[requiredValidation]" v-model="inputTitle"></v-text-field>

        <!-- 内容 -->
        <p class="text-label-large">{{ $t("craftopia.description") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
        <v-textarea :placeholder="$t('craftopia.description_placeholder')" :rules="[requiredValidation]" v-model="inputDescription"></v-textarea>

        <!-- バージョン (type != idea の場合) -->
        <template v-if="inputType !== 'idea'">
          <p class="text-label-large">{{ $t("craftopia.version") }}</p>
          <v-text-field :placeholder="$t('craftopia.blank_message')" v-model="inputVersion"></v-text-field>
        </template>

        <!-- 難易度 (type != idea の場合) -->
        <template v-if="inputType !== 'idea'">
          <p class="text-label-large">{{ $t("craftopia.difficulty") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
          <v-select placeholder="選択してください" max-width="300" :rules="[requiredValidation]" v-model="inputDifficulty"
            :items="[
              { title: $t('craftopia.enjoy'), value: 'enjoy' },
              { title: $t('craftopia.normal'), value: 'normal' },
              { title: $t('craftopia.hard'), value: 'hard' },
              { title: $t('craftopia.veryhard'), value: 'veryhard' },
              { title: $t('craftopia.hell'), value: 'hell' },
              { title: $t('craftopia.custom'), value: 'custom' },
            ]"></v-select>
        </template>

        <!-- ネットワークモード (type != idea の場合) -->
        <template v-if="inputType !== 'idea'">
          <p class="text-label-large">{{ $t("craftopia.network_mode") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span></p>
          <v-select placeholder="選択してください" max-width="300" :rules="[requiredValidation]" v-model="inputNetworkMode"
            :items="[
              { title: $t('craftopia.singleplayer'), value: 'singleplayer' },
              { title: $t('craftopia.multiplayer_host'), value: 'multiplayer_host' },
              { title: $t('craftopia.multiplayer_client'), value: 'multiplayer_guest' },
              { title: $t('craftopia.multiplayer_server'), value: 'multiplayer_server' },
              { title: $t('craftopia.multiplayer_server_client'), value: 'multiplayer_server_client' },
            ]"></v-select>
        </template>

        <!-- 再現性 (type == bug の場合) -->
        <template v-if="inputType === 'bug'">
          <p class="text-label-large">{{ $t("craftopia.reproducible") }}</p>
          <v-select placeholder="選択してください" max-width="300" v-model="inputReproducible"
            :items="[
              { title: $t('craftopia.reproducible_always'), value: 'always' },
              { title: $t('craftopia.reproducible_sometimes'), value: 'sometimes' },
              { title: $t('craftopia.reproducible_rarely'), value: 'rarely' },
              { title: $t('craftopia.reproducible_once'), value: 'once' },
            ]"></v-select>
        </template>

        <!-- プレイ人数 (type == bug かつ singleplayer でない場合) -->
        <template v-if="inputType === 'bug' && inputNetworkMode !== 'singleplayer'">
          <p class="text-label-large">{{ $t("craftopia.player_count") }}</p>
          <v-text-field :placeholder="$t('craftopia.blank_message')" v-model="inputPlayerCount" type="number"></v-text-field>
        </template>

        <!-- プロトコル (type == bug かつ singleplayer でない場合) -->
        <template v-if="inputType === 'bug' && inputNetworkMode !== 'singleplayer'">
          <p class="text-label-large">{{ $t("craftopia.protocol") }}</p>
          <v-select placeholder="選択してください" max-width="300" v-model="inputProtocol"
            :items="[
              { title: $t('craftopia.offline'), value: 'offline' },
              { title: $t('craftopia.invite_code'), value: 'invitecode' },
              { title: $t('craftopia.ip_connection'), value: 'udp' },
            ]"></v-select>
        </template>

        <!-- スクリーンショットなど (type != idea の場合) -->
        <template v-if="inputType !== 'idea'">
          <p class="text-label-large">{{ $t("craftopia.screenshots") }}</p>
          <p class="text-body-2 mb-2">多くの不具合は文字だけの説明では解決が難しく、ログファイルやセーブデータ等のファイルは問題解決に役立ちます。ご協力よろしくお願いいたします。</p>
          <v-file-input :label="$t('craftopia.screenshots_caption')" :prepend-icon="mdiImage" counter multiple
            show-size accept="image/*, video/*, .log, .txt, .zip" :rules="[fileInputValidation]" v-model="inputFiles"></v-file-input>
        </template>

        <!-- 座標 (type != idea かつ x != -1 の場合) -->
        <template v-if="inputType !== 'idea' && paramX !== -1">
          <div class="d-flex ga-4 my-2">
            <v-text-field label="X座標" v-model="paramX" type="number" readonly></v-text-field>
            <v-text-field label="Y座標" v-model="paramY" type="number" readonly></v-text-field>
            <v-text-field label="Z座標" v-model="paramZ" type="number" readonly></v-text-field>
          </div>
        </template>

        <!-- Discord公開 / 非公開キャプション -->
        <p class="text-body-2 text-red mt-2">{{ $t("craftopia.public_discord_caption") }}</p>
        <p class="text-body-2">{{ $t("craftopia.private_pocketpair_caption") }}</p>

        <!-- 連絡先 -->
        <p class="text-label-large mt-4">{{ $t("craftopia.contact") }}<span class="text-red ml-2">※{{ $t("app.required") }}</span> {{ $t("craftopia.afterday") }}</p>
        <v-radio-group v-model="inputContactType" inline>
          <v-radio label="Discord" value="Discord"></v-radio>
          <v-radio label="Twitter" value="Twitter"></v-radio>
          <v-radio label="Email" value="Email"></v-radio>
        </v-radio-group>
        <v-text-field
          :placeholder="inputContactType === 'Discord' ? $t('craftopia.contact_discord_placeholder') : inputContactType === 'Twitter' ? $t('craftopia.contact_twitter_placeholder') : 'Email'"
          :rules="[requiredValidation]" v-model="inputContact"></v-text-field>

        </template><!-- end v-if="inputType" -->

        </v-form>

        <v-progress-linear indeterminate color="cyan" v-if="isSubmitting"></v-progress-linear>
        <v-btn elevated color="cyan" class="mt-4 ml-auto d-block" :loading="isSubmitting" @click="submitForm">
          {{ $t("app.submit") }}
        </v-btn>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
import craftopiaHeader from "@/assets/img/header_craftopia.jpg";
import { mdiImage } from '@mdi/js'

export default {
  name: 'CraftopiaView',
  data() {
    return {
      craftopiaHeader,
      mdiImage,
      inputType: null,
      inputPlatform: 'steam',
      inputModUse: null,
      inputTitle: null,
      inputDescription: null,
      inputVersion: '',
      inputDifficulty: 'normal',
      inputNetworkMode: 'singleplayer',
      inputReproducible: 'once',
      inputPlayerCount: 1,
      inputProtocol: 'offline',
      inputFiles: [],
      inputContactType: 'Discord',
      inputContact: null,
      paramX: -1,
      paramY: -1,
      paramZ: -1,
      paramSystemInfo: '',
      paramSessionId: '',
      paramLog: '',
      paramGameMode: '',
      isSubmitting: false,
    }
  },
  mounted() {
    const params = new URLSearchParams(window.location.search)
    this.inputType = params.get('type') || null
    this.inputPlatform = params.get('platform') || 'steam'
    this.inputNetworkMode = params.get('networkmode') || 'singleplayer'
    this.inputPlayerCount = this.toInt(params.get('playercount'), 1)
    this.inputProtocol = params.get('protocol') || 'offline'
    this.inputVersion = params.get('version') || ''
    this.inputDifficulty = params.get('difficulty') || 'normal'
    this.paramGameMode = params.get('gameMode') || ''
    this.paramX = this.toInt(params.get('x'), -1)
    this.paramY = this.toInt(params.get('y'), -1)
    this.paramZ = this.toInt(params.get('z'), -1)
    this.paramSystemInfo = params.get('pc') || ''
    this.paramSessionId = params.get('sessionId') || ''
    this.paramLog = params.get('log') || ''
    const usingMod = params.get('usingMod')
    this.inputModUse = (usingMod?.toLowerCase() === 'true' || usingMod === '1') ? 'yes' : 'no'
  },
  methods: {
    toInt(value, fallback) {
      const n = Number.parseInt(value, 10)
      return Number.isFinite(n) ? n : fallback
    },
    requiredValidation(value) {
      return !!value || '必ず入力してください'
    },
    fileInputValidation(value) {
      if (!value || value.length === 0) return true
      const invalidFile = value.find((file) => !file.type.startsWith('image/') && !file.type.startsWith('video/') && !file.name.endsWith('.log') && !file.name.endsWith('.txt') && !file.name.toLowerCase().endsWith('.zip'))
      if (invalidFile) return `"${invalidFile.name}" は画像、動画、ログ、またはzipファイルではありません`
      if (value.length > 3) return '最大3枚まで添付可能です'
      const totalSize = value.reduce((acc, file) => acc + file.size, 0)
      if (totalSize > 30 * 1024 * 1024) return '合計30MBを超えています'
      return true
    },
    async submitForm() {
      const { valid } = await this.$refs.form.validate()
      if (!valid) return

      this.isSubmitting = true

      const safePlayerCount = this.toInt(this.inputPlayerCount, 1)
      const safeX = this.toInt(this.paramX, -1)
      const safeY = this.toInt(this.paramY, -1)
      const safeZ = this.toInt(this.paramZ, -1)

      const formData = new FormData()
      formData.append('type', this.inputType || '')
      formData.append('platform', this.inputPlatform || '')
      formData.append('title', this.inputTitle || '')
      formData.append('description', this.inputDescription || '')
      formData.append('difficulty', this.inputDifficulty || '')
      formData.append('reproducible', this.inputReproducible || '')
      formData.append('version', this.inputVersion || '')
      formData.append('network_mode', this.inputNetworkMode || '')
      formData.append('player_count', String(safePlayerCount))
      formData.append('protocol', this.inputProtocol || '')
      formData.append('x', String(safeX))
      formData.append('y', String(safeY))
      formData.append('z', String(safeZ))
      formData.append('contact', this.inputContactType + ': ' + (this.inputContact || ''))
      formData.append('session_id', this.paramSessionId || '')
      formData.append('log', this.paramLog || '')
      formData.append('system_info', this.paramSystemInfo || '')
      formData.append('modUse', this.inputModUse || '')
      formData.append('game_mode', this.paramGameMode || '')

      if (this.inputFiles) {
        this.inputFiles.forEach((file) => {
          if (file.name.toLowerCase().endsWith('.zip')) {
            formData.append('savedata', file)
            return
          }
          formData.append('screenshots', file)
        })
      }

      fetch('https://pocketpair-forms-api-dot-pocketpair.an.r.appspot.com/v1/api/form/craftopia', {
        method: 'POST',
        body: formData,
      }).then((res) => {
        if (res.status === 200) {
          this.$router.push('/submitted')
        } else {
          console.log(res.status)
        }
      }).catch((err) => {
        console.error(err)
      }).finally(() => {
        this.isSubmitting = false
      })
    },
  },
}
</script>
